import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  BroadcastPreview,
  BroadcastSourceType,
  getBroadcastPreview,
  sendBroadcast,
} from "../../services/whatsappBroadcast.service";
import { ChannelShareButtons } from "./ChannelShareButtons";

type Props = {
  sourceType: BroadcastSourceType;
  sourceId: string;
  label?: string;
  onClose: () => void;
};

const Pill: React.FC<{ ok: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${ok ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
    {children}
  </span>
);

export const WhatsAppBroadcastModal: React.FC<Props> = ({ sourceType, sourceId, label, onClose }) => {
  const [data, setData] = useState<BroadcastPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"test" | "live" | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await getBroadcastPreview(sourceType, sourceId));
    } catch (e: any) {
      setError(e?.message || "Failed to load preview");
    }
  }, [sourceType, sourceId]);

  useEffect(() => {
    load();
  }, [load]);

  const doSend = async (mode: "test" | "live") => {
    if (!data) return;
    if (mode === "live") {
      const ok = window.confirm(
        `Send this special as a WhatsApp marketing message to ${data.recipientCount} subscribed customers?\n\nThis cannot be undone and each delivered message is billed by Meta.`
      );
      if (!ok) return;
    }
    setBusy(mode);
    try {
      const r = await sendBroadcast(sourceType, sourceId, mode);
      if (mode === "test") toast.success(`Test sent to ${r.to} (${r.via})`);
      else toast.success(`Sent to ${r.sentCount} customers${r.failedCount ? `, ${r.failedCount} failed` : ""}`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Send failed", { duration: 8000 });
    } finally {
      setBusy(null);
    }
  };

  const tpl = data ? data.templates[data.templateName] : null;
  const liveDisabled = !data || data.liveBlockers.length > 0 || !!busy;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-xl p-4 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">📣 Send to WhatsApp</h3>
            {label && <p className="text-xs text-gray-500">{label}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none" aria-label="Close">
            ×
          </button>
        </div>

        {error && <div className="p-3 rounded bg-red-50 text-red-700 text-sm mb-3">{error}</div>}
        {!data && !error && <div className="py-10 text-center text-sm text-gray-500">Loading preview…</div>}

        {data && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Pill ok={data.config.enabled}>Broadcast switch: {data.config.enabled ? "ON" : "OFF"}</Pill>
              <Pill ok={tpl?.status === "APPROVED"}>
                Template {data.templateName}: {tpl?.status || "?"}
              </Pill>
              <Pill ok={data.content.isActive}>{data.content.isActive ? "Active" : "Inactive / expired"}</Pill>
              {data.record?.status === "sent" && (
                <Pill ok>
                  Sent {data.record.sentCount}/{data.record.recipientsCount}
                  {data.record.sentAt ? ` · ${new Date(data.record.sentAt).toLocaleString("en-IN")}` : ""}
                </Pill>
              )}
            </div>

            <div>
              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Customer message preview (template)</p>
              <div className="rounded-xl bg-[#e7ffdb] border border-green-100 p-3 max-w-md">
                {data.content.imageUrl && (
                  <img src={data.content.imageUrl} alt="" className="rounded-lg mb-2 max-h-48 w-full object-cover" />
                )}
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">{data.templatePreview}</pre>
              </div>
            </div>

            <div className="rounded-xl border p-3 text-sm">
              <p className="font-bold text-gray-900">
                Recipients: {data.recipientCount} subscribed customer{data.recipientCount === 1 ? "" : "s"}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Opted in {data.stats.optedIn} · active tiffin subscribers {data.stats.activeSubscribers} · opted out {data.stats.optedOut} ·
                not opted in {data.stats.notConsented} (of {data.stats.uniquePhones} numbers). Max {data.config.maxRecipients} per send,{" "}
                {data.config.perUserDailyCap}/customer/day.
              </p>
              {data.recipientsSample.length > 0 && (
                <p className="text-[11px] text-gray-400 mt-1">
                  {data.recipientsSample.map((r) => `${r.name || r.phone} (${r.reason})`).join(", ")}
                </p>
              )}
            </div>

            {data.liveBlockers.length > 0 && (
              <ul className="text-xs text-amber-800 bg-amber-50 rounded-lg p-3 list-disc pl-5">
                {data.liveBlockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}

            {data.record?.status === "sent" && (
              <p className="text-xs text-gray-600">
                Delivered {data.record.deliveredCount ?? 0} · read {data.record.readCount ?? 0} · not delivered{" "}
                {data.record.deliveryFailedCount ?? 0}
                {data.record.deliveryFailuresByCode
                  ? ` (${Object.entries(data.record.deliveryFailuresByCode).map(([k, v]) => `${k}: ${v}`).join(", ")}; 131049 = Meta marketing frequency cap)`
                  : ""}
              </p>
            )}
            {data.record?.lastTest && (
              <p className="text-xs text-gray-500">
                Last test: {data.record.lastTest.ok ? "✅" : "❌"} to {data.record.lastTest.to} via {data.record.lastTest.via}
                {data.record.lastTest.at ? ` at ${new Date(data.record.lastTest.at).toLocaleString("en-IN")}` : ""}
                {data.record.lastTest.deliveryStatus ? ` · WhatsApp status: ${data.record.lastTest.deliveryStatus}` : ""}
                {data.record.lastTest.errorCode ? ` (${data.record.lastTest.errorCode} ${data.record.lastTest.errorTitle || ""})` : ""}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!!busy || !data.myPhone}
                onClick={() => doSend("test")}
                className="px-3 py-2 rounded-lg border text-sm font-semibold bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === "test" ? "Sending…" : `Send test to me${data.myPhone ? ` (${data.myPhone})` : ""}`}
              </button>
              {data.canLiveSend && (
                <button
                  type="button"
                  disabled={liveDisabled}
                  onClick={() => doSend("live")}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {busy === "live" ? "Sending…" : `Send to ${data.recipientCount} customers`}
                </button>
              )}
            </div>

            <div className="border-t pt-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp Channel post</p>
              <p className="text-[11px] text-gray-500 mb-2">
                Meta has no API for posting to Channels. Copy or share this, then choose the Ruchira channel in WhatsApp.
              </p>
              <pre className="whitespace-pre-wrap font-sans text-sm bg-gray-50 border rounded-lg p-3 max-h-56 overflow-y-auto mb-2">
                {data.channelText}
              </pre>
              <ChannelShareButtons text={data.channelText} imageUrl={data.content.imageUrl} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppBroadcastModal;
